/**
 * Power-up pickups (FS-07 §2.5, §2.6). Until now power-ups arrived on a timer — `grantTimer` in
 * game.ts — which meant the eight power-ups had no presence in the playfield at all and nothing to
 * learn. Pickups are objects you fly into.
 *
 * The legibility rules (Amendment PT-1) are geometry, not decoration:
 *  - a pickup is the only **closed convex** thing in the playfield (§5.5), so it separates from
 *    every hazard silhouette in greyscale;
 *  - it is the only thing that **spins on its own axis** independent of scroll (the motion channel);
 *  - it draws an affordance ring at exactly `PICKUP_RADIUS`, so what is drawn is what collects;
 *  - passing one uncollected fires a miss cue, because silence teaches nothing (R38).
 *
 * Placement obeys R29: never inside a node's centre 25 % (that would make node-centre awards and
 * pickups compete) and never closer than `PICKUP_MIN_HAZARD_CLEARANCE` to a hazard's lethal region.
 */
import { CULL_Z, PLAY_RADIUS, SPAWN_Z } from '../core/constants';
import type { EventBus } from '../core/events';
import type { EventMap, PowerUpName } from '../core/event-map';
import type { Rng } from '../core/rng';
import { createPool, type Pool, type Poolable } from '../core/pool';

/** FS-07 §5.3 / §5.5. */
export const PICKUP_RADIUS = 1.1; // u — collection radius, swept
export const PICKUP_CAP = 6;
export const PICKUP_IDLE_SPIN = 0.8; // rev/s — cosmetic only, never feeds sim state
export const LURE_RANGE = 60; // u of Z at which the lead-in line appears
export const PICKUP_MIN_HAZARD_CLEARANCE = 2.0; // u

export class Pickup implements Poolable {
  poolIndex = 0;
  kind: PowerUpName = 'Harmonic Shield';
  /** Tunnel-polar placement, resolved at spawn. */
  theta = 0;
  r = 0;
  z = 0;
  prevZ = 0;
  /** Cosmetic spin phase — never read by the sim (Design Bible §4: cosmetic jitter never feeds back). */
  spin = 0;
  /** First pickup this profile has ever seen — drives the emphasis treatment (FS-07 R36). */
  firstEver = false;
  collected = false;
  /** Latched so a miss fires exactly once, on the frame it passes the ship. */
  missed = false;
  active = false;

  poolReset(): void {
    this.active = false;
    this.collected = false;
    this.missed = false;
    this.firstEver = false;
    this.spin = 0;
  }
}

export class Pickups {
  readonly pool: Pool<Pickup> = createPool('pickups', PICKUP_CAP, () => new Pickup());

  constructor(
    private bus: EventBus<EventMap>,
    private rng: Rng,
  ) {}


  reset(): void {
    this.pool.releaseAll();
  }

  /** Re-seed for a new run. The pool itself is allocated once, at construction. */
  reseed(rng: Rng): void {
    this.rng = rng;
    this.reset();
  }

  /**
   * Spawn one pickup at `SPAWN_Z`. `nodeTheta` is the arriving chart position the pickup must stay
   * clear of (R29): it is placed on the opposite side of the node's centre band, so collecting one
   * is a deliberate line rather than a thing that happens to you.
   */
  spawn(kind: PowerUpName, nodeTheta: number, nodeHalfTheta: number, firstEver: boolean): Pickup | null {
    const p = this.pool.acquire();
    if (!p) return null;
    // Offset past the node's centre 25 % but inside the passable arc where possible.
    const side = this.rng.next() < 0.5 ? -1 : 1;
    const offset = Math.max(nodeHalfTheta * 0.6, 0.18);
    p.kind = kind;
    p.theta = nodeTheta + side * offset;
    p.r = 3.0 + this.rng.next() * (PLAY_RADIUS - 4.0);
    p.z = SPAWN_Z;
    p.prevZ = SPAWN_Z;
    p.firstEver = firstEver;
    p.collected = false;
    p.missed = false;
    p.active = true;
    return p;
  }

  /**
   * Advance, test collection against the ship's swept segment, cull past `CULL_Z`.
   * Collection uses the same swept form as FS-05 §4.4 so a pickup cannot be tunnelled at tier 5
   * (R30): the ship's [prevZ, z] slab is tested, not its instantaneous position.
   */
  step(
    dz: number,
    shipX: number,
    shipY: number,
    onCollect: (p: Pickup) => void,
  ): void {
    const items = this.pool.items;
    for (let i = 0; i < items.length; i++) {
      const p = items[i]!;
      if (!p.active) continue;
      p.prevZ = p.z;
      p.z += dz;

      // Crossing the ship plane this step?
      if (p.prevZ <= 0 && p.z >= 0) {
        const px = Math.sin(p.theta) * p.r;
        const py = Math.cos(p.theta) * p.r;
        const d = Math.hypot(px - shipX, py - shipY);
        if (d <= PICKUP_RADIUS) {
          p.collected = true;
          onCollect(p);
          this.bus.emit('pickup:collected', { powerup: p.kind, firstEver: p.firstEver });
          this.pool.release(p);
          continue;
        }
        if (!p.missed) {
          // R38 — a pickup that passes uncollected must say so, or the player who avoided it
          // never learns it was collectable.
          p.missed = true;
          this.bus.emit('pickup:missed', { powerup: p.kind });
        }
      }

      if (p.z > CULL_Z) {
        this.pool.release(p);
      }
    }
  }
}
